import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LimitsContext = createContext(null);

export function LimitsProvider({ children }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const refresh = useCallback(async () => {
    if (!user) {
      setData(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/api/auth/me/limits");
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <LimitsContext.Provider value={{ data, loading, refresh }}>
      {children}
    </LimitsContext.Provider>
  );
}

/**
 * Hook to access plan limits.
 * Returns:
 *   plan: { plan_name, plan_slug, is_paid }
 *   limits: { max_trips: -1, can_import_gpx: false, ... }
 *   usage: { trips: 4, clubs_created: 1 }
 *   check(feature_key, currentCount?) -> { allowed, limit, current, remaining, pct }
 *   refresh() -> re-fetch limits
 */
export function useLimits() {
  const ctx = useContext(LimitsContext);
  if (!ctx) return { plan: null, limits: {}, usage: {}, check: () => ({ allowed: true }), refresh: () => {} };

  const { data, loading, refresh } = ctx;
  const limits = data?.limits || {};
  const usage = data?.usage || {};
  const plan = data?.plan || null;
  const safetyNet = data?.safety_net || {};

  // Effective max for a feature, resolving unlimited (-1) to the internal
  // anti-abuse safety-net cap. Returns Infinity when truly unlimited.
  const effectiveCap = useCallback((featureKey) => {
    const limit = limits[featureKey];
    if (limit === -1 || limit === undefined || limit === null) {
      const cap = safetyNet[featureKey];
      return cap && cap > 0 ? cap : Infinity;
    }
    return limit;
  }, [limits, safetyNet]);

  const check = useCallback((featureKey, currentCount) => {
    const limit = limits[featureKey];
    // Boolean feature
    if (typeof limit === "boolean") {
      return { allowed: limit, limit, feature: featureKey };
    }
    // Numeric feature
    if (limit === undefined || limit === null) return { allowed: true };
    if (limit === -1) return { allowed: true, limit: -1, unlimited: true };
    const count = currentCount ?? 0;
    const remaining = Math.max(0, limit - count);
    const pct = limit > 0 ? (count / limit) * 100 : 0;
    return {
      allowed: count < limit,
      limit,
      current: count,
      remaining,
      pct,
      warning: pct >= 80 && pct < 100,
      feature: featureKey,
    };
  }, [limits]);

  return { plan, limits, usage, safetyNet, effectiveCap, check, refresh, loading };
}
